<template>
    <div class="text-center pb-5" :style="{'min-height': '10rem'}">
        <b-container v-if="cardReaderOnly" class="px-3 pt-3 mb-5 pb-2 bg-light" :style="{'border-bottom': '1px solid grey'}">
            <h5 class="text-left">Card Reader Pay</h5>
        </b-container>

        <div class="font-weight-bold h4 mb-4 mt-5" v-if="order.Total && isKioskOrder">Total Payable: ${{order.Total.toFixed(2)}}</div>
        <div class="font-weight-bold h4 mb-4 mt-5" v-if="!isKioskOrder && order.RestaurantTotal">Total Payable: ${{order.RestaurantTotal.toFixed(2)}}</div>
        <p v-if="!isKioskOrder && isNaN(order.Total)" class="font-weight-bold h4 mb-4">Total Payable: <b-spinner class="mb-2" small label="Medium Spinner"></b-spinner></p>

        <div class="mb-4">
            {{ message }}
        </div>

        <div class="mb-4 text-danger font-weight-bold" style="font-size:larger" id="flashText" v-if="showNotif">
            Ask customer to INSERT or TAP CARD when prompted on the reader
        </div>

        <!-- Timeout progress bar -->
        <div v-if="polling" class="mb-3 px-5">
            <b-progress :value="timeoutProgress" :max="100" height="6px" variant="warning" animated/>
            <small class="text-muted">Time remaining: {{ timeoutRemaining }}s</small>
        </div>

        <div class="mb-4 px-4 text-danger font-weight-bold" v-if="errorMessage">
            {{ errorMessage }}
        </div>

        <div>
            <!-- Manual start button when not auto-started -->
            <b-btn
                v-if="!cardReaderOnly && canStartPayment && !errorMessage"
                variant="warning"
                class="font-weight-bold"
                @click="startPayment"
            >
                Connect to Reader
            </b-btn>

            <!-- Retry after a failed attempt: cancels the old payment first, then starts a new one -->
            <b-btn
                size="lg"
                variant="danger"
                v-if="canRetry"
                @click="retryPayment"
                class="px-4 mx-2 font-weight-bold"
            >
                Try Again
            </b-btn>

            <b-btn
                size="lg"
                variant="warning"
                v-if="canShowNeverMind"
                :disabled="loading || cancelling"
                @click="cancelConfirmation"
                class="px-4 mx-2 font-weight-bold"
            >
                <span v-if="cancelling">
                    Stopping reader...
                    <b-spinner small class="ml-2 mb-1" label="Stopping reader"/>
                </span>
                <span v-else>Never mind</span>
            </b-btn>

            <b-spinner v-if="loading" class="mb-2 ml-2" small label="Loading Spinner"/>
        </div>
    </div>
</template>

<script>
    const POLL_INTERVAL_MS = 2000
    const TIMEOUT_SECONDS = 75
    const CANCEL_TIMEOUT_MS = 20000
    const CANCEL_FAILED_MESSAGE = 'Could not stop the card reader. Try again.'
    const CANCEL_RESULTS = ['canceled', 'nothing_to_cancel', 'authorized', 'succeeded']
    const RESULT_BY_PAYMENT_STATUS = { canceled: 'canceled', requires_capture: 'authorized', succeeded: 'succeeded' }

    export default {
        name: "PayCardReader",
        props: {
            order: { required: true },
            selectedReader: { required: false, default: false },
            cardReaderOnly: { type: Boolean, default: false },
            kioskUsage: { type: Number, default: 0 },
        },
        data () {
            return {
                loading: false,
                polling: false,
                message: ' ',
                errorMessage: null,
                phoneOrder: null,
                showNotif: false,
                canShowNeverMind: true,
                paymentIntentId: null,
                pollTimer: null,
                countdownTimer: null,
                timeoutTimer: null,
                timeoutRemaining: TIMEOUT_SECONDS,
                timeoutProgress: 100,
                readerID: null,
                cancelling: false,
                completing: false,
                paymentSettled: false,
                isDestroyed: false,
            }
        },
        computed: {
            isKioskOrder () {
                return this.order.TabNo && this.order.TabNo.substring(0, 1) !== 'R'
            },
            restaurantId () {
                return this.$store.state.selectedRestaurantId
            },
            // A new payment may only start once the previous one is released (paymentIntentId cleared)
            canStartPayment () {
                return !this.polling && !this.loading && !this.cancelling && !this.completing && !this.paymentIntentId
            },
            // While waiting too: the reader may reject a card (e.g. expired) and keep waiting without reporting a failure
            canRetry () {
                return (this.polling || !!this.errorMessage) && !this.loading && !this.cancelling && !this.completing && !this.paymentSettled
            },
        },
        created () {
            // In-flight cancelReaderPayment() promise, shared so concurrent callers get the same outcome
            this.cancelRequest = null
            this.lastStatusLog = null
            if (this.cardReaderOnly) {
                this.startPayment()
            }
            if(this.selectedReader){
                this.getReaderID()
            }
        },
        beforeDestroy () {
            this.isDestroyed = true
            // Parents should await cancelReaderPayment() before hiding this component. If we are
            // unmounted with a payment still open anyway, release the reader so it isn't left armed.
            if (this.paymentIntentId && !this.paymentSettled && !this.cancelling && !this.completing) {
                this.cancelReaderPayment()
            } else {
                this.stopPolling()
            }
        },
        methods: {
            async getReaderID () {
                const model = {
                    RestaurantID: this.restaurantId,
                    TableIKey: -1,
                }
                const res = await this.$api.getRestaurant.GetRestaurantPOSs(model)
                if (res.success) {
                    const reader = res.results.find(reader => reader.POSName === this.selectedReader) || res.results[0]
                    if (reader) {
                        this.readerID = reader.IKey
                    }
                }
            },
            async startPayment () {
                // One payment at a time: a second start would push a second payment and a second polling loop
                if (this.loading || this.polling || this.cancelling || this.completing) return
                if (this.paymentIntentId && !this.paymentSettled) return

                this.errorMessage = null
                this.loading = true
                this.paymentSettled = false
                this.message = 'Creating payment...'

                await this.getCurrentOrder()

                const intentData = await this.CreatePaymentIntent()
                if (!intentData) {
                    this.loading = false
                    return
                }

                // Parse PI id from client_secret: "pi_XXX_secret_YYY" → "pi_XXX"
                this.paymentIntentId = intentData.client_secret.split('_secret_')[0]

                // Screen was closed while the payment was being created
                if (this.isDestroyed) {
                    this.cancelReaderPayment()
                    return
                }

                const pushed = await this.pushToReader(this.paymentIntentId)
                if (this.isDestroyed) {
                    this.cancelReaderPayment()
                    return
                }
                if (!pushed) {
                    // Don't leave the new payment open ("Incomplete") in Stripe
                    await this.releaseAfterReaderError(this.errorMessage)
                    this.loading = false
                    return
                }

                this.loading = false
                this.showNotif = true
                this.message = 'Waiting for customer to tap or insert card...'
                this.startPolling()
            },

            async pushToReader (paymentIntentId) {
                const model = {
                    RestaurantID: this.restaurantId,
                    KioskUsage: this.kioskUsage,
                    PaymentIntentId: paymentIntentId,
                    ReaderID: this.readerID,
                    SkipTipping:  false,
                    OrderID: this.order.OrderID,
                }

                
                try {
                    const res = await this.$api.getRestaurant.ProcessPaymentIntent(model)
                    if (!res.success) {
                        this.errorMessage = 'Failed to send payment to reader. please make sure the reader is online and connected to the internet.'
                        return false
                    }
                    return true
                } catch {
                    this.errorMessage = 'Could not reach the reader. Please try again.'
                    return false
                }
            },

            startPolling () {
                // Never run two polling loops at once: a replaced loop would keep calling GetReaderStatus forever
                this.stopPolling()
                this.polling = true
                this.lastStatusLog = null
                this.timeoutRemaining = TIMEOUT_SECONDS
                this.timeoutProgress = 100

                // Countdown tick every second for UI
                this.countdownTimer = setInterval(() => {
                    this.timeoutRemaining = Math.max(0, this.timeoutRemaining - 1)
                    this.timeoutProgress = Math.round((this.timeoutRemaining / TIMEOUT_SECONDS) * 100)
                }, 1000)

                // Poll every 2 seconds; a loop that is no longer the current one ends itself
                const pollTimer = setInterval(() => {
                    if (this.pollTimer !== pollTimer) { clearInterval(pollTimer); return }
                    this.pollStatus()
                }, POLL_INTERVAL_MS)
                this.pollTimer = pollTimer

                // Hard timeout
                this.timeoutTimer = setTimeout(async () => {
                    const outcome = await this.cancelReaderPayment()
                    if (outcome === 'released') {
                        this.errorMessage = 'Payment timed out. The reader has been reset.'
                    }
                }, TIMEOUT_SECONDS * 1000)
            },

            stopPolling () {
                this.polling = false
                if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
                if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null }
                if (this.timeoutTimer) { clearTimeout(this.timeoutTimer); this.timeoutTimer = null }
            },

            async pollStatus () {
                const model = {
                    RestaurantID: this.restaurantId,
                    KioskUsage: this.kioskUsage,
                    ReaderID: this.readerID,
                }
                try {
                    const res = await this.$api.getRestaurant.GetReaderStatus(model)
                    this.logReaderStatus(res)
                    // Ignore answers that arrive after polling stopped (cancel, timeout, or an earlier poll)
                    if (!this.polling) return

                    if (!res.success) {
                        return this.releaseAfterReaderError(res.message || 'Reader status check failed.')
                    }

                    const status = res.action_status
                    if (status === 'succeeded') {
                        this.stopPolling()
                        this.showNotif = false
                        this.message = 'Payment captured. Processing...'
                        this.completePayment(this.paymentIntentId, true)
                    } else if (status === 'failed') {
                        return this.releaseAfterReaderError(res.failure_message || 'Payment failed on the reader.')
                    }
                    // status === 'in_progress' → keep polling
                } catch (err) {
                    // Network hiccup — keep polling, don't abort
                    console.warn('[CardReader] GetReaderStatus request failed', err)
                }
            },

            // Logs the reader status whenever it changes, to trace reader payments in the browser console
            logReaderStatus (res) {
                const entry = JSON.stringify(res)
                if (entry !== this.lastStatusLog) {
                    this.lastStatusLog = entry
                    console.info('[CardReader] GetReaderStatus', this.paymentIntentId, entry)
                }
            },

            async releaseAfterReaderError (reason) {
                const outcome = await this.cancelReaderPayment()
                if (outcome === 'released') {
                    this.errorMessage = reason
                } else if (outcome === 'failed') {
                    this.errorMessage = `${reason} ${this.errorMessage}`
                }
            },

            CancelReaderAction (paymentIntentId) {
                const model = {
                    RestaurantID: this.restaurantId,
                    KioskUsage: this.kioskUsage,
                    ReaderID: this.readerID,
                }
                if (paymentIntentId) {
                    model.PaymentIntentId = paymentIntentId
                }
                return this.$api.getRestaurant.CancelReaderAction(model)
            },

            // The one way out of a reader payment. Every exit (Never mind, timeout, reader error,
            // unmount, parents switching away) goes through here and must act on the outcome:
            //   'released' – nothing was charged; safe to close or switch away
            //   'paid'     – the card was already approved; the payment is completed here instead
            //   'failed'   – the reader could not be stopped; keep this screen open
            cancelReaderPayment () {
                if (!this.cancelRequest) {
                    const clear = () => { this.cancelRequest = null }
                    this.cancelRequest = this.runCancelReaderPayment()
                    this.cancelRequest.then(clear, clear)
                }
                return this.cancelRequest
            },

            async runCancelReaderPayment () {
                // Already approved and being (or done being) recorded — nothing to cancel
                if (this.paymentSettled || this.completing) {
                    return 'paid'
                }

                // No open payment (never created, or already canceled): nothing_to_cancel, close as today
                if (!this.paymentIntentId) {
                    this.stopPolling()
                    return 'released'
                }

                this.stopPolling()
                this.showNotif = false
                this.errorMessage = null
                this.message = 'Stopping the card reader...'
                this.cancelling = true

                const paymentIntentId = this.paymentIntentId
                let res = null
                try {
                    // Don't leave staff stuck on "Stopping reader..." if the request never comes back
                    const timedOut = new Promise(resolve => setTimeout(() => resolve(null), CANCEL_TIMEOUT_MS))
                    res = await Promise.race([this.CancelReaderAction(paymentIntentId), timedOut])
                } catch (err) {
                    console.warn('[CardReader] CancelReaderAction request failed', err)
                    res = null
                }
                console.info('[CardReader] CancelReaderAction', paymentIntentId, res ? JSON.stringify(res) : 'no response')
                this.cancelling = false

                if (!res || !res.success) {
                    return this.cancelFailed(res)
                }

                const result = this.readCancelResult(res)
                if (result === 'canceled' || result === 'nothing_to_cancel') {
                    this.paymentIntentId = null
                    this.message = ' '
                    return 'released'
                }
                if (result === 'authorized' || result === 'succeeded') {
                    this.message = 'Card was already approved. Completing payment...'
                    const completed = await this.completePayment(paymentIntentId, result === 'authorized')
                    if (completed) {
                        this.$showSuccess('Card was already approved — payment completed.')
                    }
                    return 'paid'
                }
                return this.cancelFailed(res)
            },

            // Normalizes the CancelReaderAction result, falling back to the payment status when it is unrecognized
            readCancelResult (res) {
                const result = String(res.result || '').toLowerCase()
                if (result === 'cancelled') return 'canceled'
                if (CANCEL_RESULTS.includes(result)) return result
                const fromStatus = RESULT_BY_PAYMENT_STATUS[String(res.paymentIntentStatus || '').toLowerCase()]
                if (fromStatus) return fromStatus
                // Backends without the PaymentIntentId contract send no result: same as nothing_to_cancel
                return result ? 'unknown' : 'nothing_to_cancel'
            },

            cancelFailed (res) {
                this.message = ' '
                // Show the server's reason so staff (and we) can see why the reader could not be stopped
                const reason = !res ? 'no response from server' : (res.message || res.result || '')
                this.errorMessage = reason ? `${CANCEL_FAILED_MESSAGE} (${reason})` : CANCEL_FAILED_MESSAGE
                if (this.isDestroyed) {
                    this.$showError('Could not stop the card reader. Check the reader before taking another payment.')
                }
                return 'failed'
            },

            async retryPayment () {
                const outcome = await this.cancelReaderPayment()
                if (outcome === 'released') {
                    this.startPayment()
                }
            },

            async cancelConfirmation () {
                if (this.cancelling) return
                const outcome = await this.cancelReaderPayment()
                if (outcome !== 'released') return
                this.$emit('cancelConfirmation')
                try { this.$emit('cancel') } catch { /* ignore */ }
            },

            getCurrentOrder () {
                const body = {
                    RestaurantID: this.restaurantId,
                    OrderID: this.order.OrderID,
                }
                return this.$api.getRestaurant.GetRestaurantCurrentOrders(body).then(res => {
                    if (res.success) {
                        this.phoneOrder = res.orderInfo.PhoneOrder
                        return res
                    }
                }).catch(() => {})
            },

            CreatePaymentIntent () {
                const model = {
                    RestaurantID: this.restaurantId,
                    OrderID: this.order.OrderID,
                }
                return this.$api.getRestaurant.CreatePaymentIntent(model).then(res => {
                    if (res.success) {
                        return { client_secret: res.client_secret, subtotal: res.subtotal }
                    }
                    this.errorMessage = res.message || 'Failed to create payment.'
                    return null
                }).catch(() => {
                    this.errorMessage = 'Failed to create payment.'
                    return null
                })
            },

            // Capture (when the reader only authorized) and record the payment on the order
            async completePayment (id, needsCapture) {
                this.completing = true
                this.loading = true
                this.canShowNeverMind = false
                const captured = needsCapture ? await this.CapturePaymentIntent(id) : true
                const completed = captured && await this.UpdateTransaction(id)
                this.completing = false
                return completed
            },

            CapturePaymentIntent (id) {
                const model = {
                    RestaurantID: this.restaurantId,
                    ID: id,
                }
                return this.$api.getRestaurant.CapturePaymentIntent(model).then(res => {
                    this.loading = false
                    if (res.success) {
                        return true
                    }
                    this.canShowNeverMind = true
                    this.errorMessage = res.message || 'Capture failed.'
                    return false
                }).catch(() => {
                    this.loading = false
                    this.canShowNeverMind = true
                    this.errorMessage = 'Capture request failed.'
                    return false
                })
            },

            UpdateTransaction (id) {
                this.loading = true
                this.canShowNeverMind = false
                const model = {
                    RestaurantID: this.restaurantId,
                    OrderID: this.order.OrderID,
                    phoneNumber: this.phoneOrder,
                    ReceiptNumber: id,
                    PaymentMmethod: 'card',
                }
                return this.$api.getRestaurant.UpdateTransaction(model).then(res => {
                    this.loading = false
                    this.canShowNeverMind = true
                    if (res.success) {
                        this.paymentSettled = true
                        if (this.cardReaderOnly) {
                            this.$emit('paymentCompleted')
                        } else {
                            this.$emit('cancelConfirmation')
                            this.$emit('closeAllPopUp')
                        }
                        return true
                    }
                    // Shown on screen too so Try Again appears (it re-checks the payment and records it again)
                    this.errorMessage = res.message || 'Could not record the payment on the order.'
                    this.$showError(res.message)
                    return false
                }).catch(err => {
                    this.loading = false
                    this.canShowNeverMind = true
                    this.errorMessage = 'Could not record the payment on the order.'
                    this.$showError('Error in updateTransaction: ' + err)
                    return false
                })
            },
        },
    }
</script>

<style scoped>
    @keyframes flash {
        0% { opacity: 1 }
        50% { opacity: 0 }
        100% { opacity: 1 }
    }
    #flashText {
        animation-name: flash !important;
        animation-iteration-count: infinite !important;
        animation-fill-mode: forwards;
        animation-duration: 2s !important;
    }
</style>
